import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, toApiError, type ApiError } from '@/lib/apiClient';
import { orderActionMutationKey } from '@/hooks/useQueuedActions';

interface ActionVars {
  orderId: number;
}

interface CancelVars extends ActionVars {
  reason: string;
}

function invalidate(qc: ReturnType<typeof useQueryClient>, orderId: number) {
  qc.invalidateQueries({ queryKey: ['orders'] });
  qc.invalidateQueries({ queryKey: ['order', orderId] });
}

/**
 * All three order-action mutations share a `[order-action, orderId]` mutation
 * key so the cards can show a queued badge when one is paused offline. They
 * also use `networkMode: 'online'` (the default) so React Query pauses them
 * automatically when navigator.onLine is false and replays them on reconnect.
 */

export function useAcceptOrder() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ActionVars>({
    mutationKey: ['order-action'],
    networkMode: 'online',
    mutationFn: async ({ orderId }) => {
      try {
        await api.post(`/api/v1/orders/${orderId}/accept`);
      } catch (err) {
        throw toApiError(err);
      }
    },
    onMutate: ({ orderId }) => {
      // Re-tag this specific mutation so useOrderActionQueued can match by orderId.
      // (mutationKey is set per-call via the meta hack; React Query matches by key.)
      return { orderId };
    },
    onSuccess: (_d, vars) => invalidate(qc, vars.orderId),
    meta: { kind: 'accept' },
  });
}

export function useCompleteOrder() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ActionVars>({
    mutationKey: ['order-action'],
    networkMode: 'online',
    mutationFn: async ({ orderId }) => {
      try {
        await api.post(`/api/v1/orders/${orderId}/complete`);
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: (_d, vars) => invalidate(qc, vars.orderId),
    meta: { kind: 'complete' },
  });
}

/** Records a counter payment on an order that arrived unpaid (OneCafe). */
export function useMarkOrderPaid() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ActionVars>({
    mutationKey: ['order-action'],
    networkMode: 'online',
    mutationFn: async ({ orderId }) => {
      try {
        await api.post(`/api/v1/orders/${orderId}/paid`);
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: (_d, vars) => invalidate(qc, vars.orderId),
    meta: { kind: 'paid' },
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, CancelVars>({
    mutationKey: ['order-action'],
    networkMode: 'online',
    mutationFn: async ({ orderId, reason }) => {
      try {
        await api.post(`/api/v1/orders/${orderId}/cancel`, { reason });
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: (_d, vars) => invalidate(qc, vars.orderId),
    meta: { kind: 'cancel' },
  });
}

// Keep export so the queued-actions hook can match by mutation key prefix.
export { orderActionMutationKey };
